import React from "react";
import { AlertCircle, CheckCircle2, Clock, LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface TicketOptionPreviewProps {
  label: string;
  status?: string;
  priority?: string;
  contact_name?: string;
  assigned_to_name?: string;
}

export const getTicketStatusColor = (status?: string): string => {
  switch (status) {
    case "new":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
    case "assigned":
      return "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200";
    case "in_progress":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
    case "waiting_for_customer":
    case "waiting_for_vendor":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200";
    case "resolved":
      return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
    case "cancelled":
      return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
    case "closed":
    default:
      return "bg-card-100 text-gray-800 dark:text-gray-200";
  }
};

interface PriorityIconDef {
  icon: LucideIcon;
  color: string;
}

export const getTicketPriorityIcon = (priority?: string): PriorityIconDef => {
  switch (priority) {
    case "critical":
      return { icon: AlertCircle, color: "text-red-600" };
    case "urgent":
      return { icon: AlertCircle, color: "text-red-500" };
    case "high":
      return { icon: AlertCircle, color: "text-orange-500" };
    case "medium":
      return { icon: Clock, color: "text-yellow-500" };
    case "low":
      return { icon: CheckCircle2, color: "text-green-500" };
    default:
      return { icon: Clock, color: "text-gray-500" };
  }
};

export function TicketOptionPreview({
  label,
  status,
  priority,
  contact_name,
  assigned_to_name,
}: TicketOptionPreviewProps) {
  const { icon: PriorityIcon, color: priorityColor } = getTicketPriorityIcon(priority);

  return (
    <div className="flex flex-col gap-1.5 w-full">
      <div className="flex items-center gap-2">
        <span className="font-medium">{label}</span>
        {status && (
          <span
            className={cn(
              "text-xs px-2 py-0.5 rounded-full",
              getTicketStatusColor(status)
            )}
          >
            {status}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {priority && (
          <div className="flex items-center gap-1">
            <PriorityIcon className={cn("h-3 w-3 flex-shrink-0", priorityColor)} />
            <span className="capitalize">{priority}</span>
          </div>
        )}
        {contact_name && (
          <div className="flex items-center gap-1 min-w-0">
            <span className="truncate">{contact_name}</span>
          </div>
        )}
        {assigned_to_name && (
          <div className="flex items-center gap-1 min-w-0">
            <span className="truncate">→ {assigned_to_name}</span>
          </div>
        )}
      </div>
    </div>
  );
}
